import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import {
  approveInfoScreenDevice,
  createInfoScreenDisplay,
  deactivateInfoScreenDisplay,
  deleteInfoScreenDevice,
  fetchInfoScreenDisplays,
  revokeInfoScreenDevice,
  updateInfoScreenDeviceLabel,
  updateInfoScreenDisplay,
} from "@/lib/infoScreenApi";
import { useCurrentUser } from "@/lib/auth";
import { formatDateTimeShort } from "@/lib/bookingDates";
import { canWriteStammdaten } from "@/lib/permissions";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";
import type { InfoScreenDevice, InfoScreenDisplay } from "@/types/infoScreen";

function formatDeviceTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return formatDateTimeShort(iso);
}

function deviceStatusLabel(status: string): string {
  if (status === "approved") return "Freigeschaltet";
  if (status === "revoked") return "Gesperrt";
  return "Wartet auf Freigabe";
}

function slugifyCode(label: string, index: number): string {
  const base = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return base ? `${base}-${index}` : `display-${index}`;
}

export function InfoScreenDisplaysPanel() {
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteStammdaten(groups);

  const [displays, setDisplays] = useState<InfoScreenDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchInfoScreenDisplays();
      setDisplays(rows.filter((d) => d.is_active).sort((a, b) => a.sort_order - b.sort_order));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveLabel(display: InfoScreenDisplay, label: string) {
    if (!canEdit || label.trim() === display.label) return;
    setSavingId(display.id);
    setError(null);
    try {
      await updateInfoScreenDisplay(display.id, { label: label.trim() });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen");
    } finally {
      setSavingId(null);
    }
  }

  async function addDisplay() {
    if (!canEdit) return;
    const label = newLabel.trim();
    if (!label) {
      setError("Bitte einen Namen für den Bildschirm eingeben.");
      return;
    }
    setSavingId("new");
    setError(null);
    try {
      const code = slugifyCode(label, displays.length + 1);
      await createInfoScreenDisplay({
        code,
        label,
        sort_order: (displays.length + 1) * 10,
      });
      setNewLabel("");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Bildschirm konnte nicht angelegt werden");
    } finally {
      setSavingId(null);
    }
  }

  async function handleDeviceAction(action: () => Promise<unknown>, deviceId: string) {
    setSavingId(deviceId);
    setError(null);
    try {
      await action();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Aktion fehlgeschlagen");
    } finally {
      setSavingId(null);
    }
  }

  async function saveDeviceLabel(device: InfoScreenDevice, label: string) {
    if (!canEdit || label.trim() === (device.label ?? "")) return;
    await handleDeviceAction(
      () => updateInfoScreenDeviceLabel(device.id, label.trim() || null),
      device.id,
    );
  }

  async function removeDisplay(display: InfoScreenDisplay) {
    if (!canEdit) return;
    if (!window.confirm(`Bildschirm „${display.label}“ wirklich entfernen?`)) return;
    setSavingId(display.id);
    setError(null);
    try {
      await deactivateInfoScreenDisplay(display.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Entfernen fehlgeschlagen");
    } finally {
      setSavingId(null);
    }
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Bildschirme werden geladen…</p>;
  }

  return (
    <div className="space-y-6">
      {!canEdit ? <StammdatenWriteHint /> : null}

      <Card title="Physische Infobildschirme">
        <div className="space-y-4">
          <p className="text-sm text-label-secondary">
            Jeder Standort hat eine eigene URL, z. B.{" "}
            <code className="rounded bg-fill px-1 text-xs">/info-screen/lobby-1</code>. Beim ersten
            Öffnen muss das Gerät hier freigeschaltet werden.
          </p>

          {displays.length > 0 ? (
            <ul className="space-y-3">
              {displays.map((display) => (
                <li
                  key={display.id}
                  className="flex flex-col gap-2 rounded-xl border border-separator bg-fill/20 p-4 sm:flex-row sm:items-end"
                >
                  <label className="flex min-w-0 flex-1 flex-col gap-1 text-sm">
                    Anzeigename
                    <input
                      className="eh-input"
                      disabled={!canEdit || savingId === display.id}
                      defaultValue={display.label}
                      onBlur={(e) => void saveLabel(display, e.target.value)}
                    />
                  </label>
                  <div className="flex flex-col gap-1 text-xs text-label-secondary sm:w-48">
                    <span>Code (URL)</span>
                    <code className="truncate rounded bg-fill px-2 py-1.5">{display.code}</code>
                  </div>
                  <a
                    href={`/info-screen/${encodeURIComponent(display.code)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-accent underline sm:pb-2"
                  >
                    Öffnen
                  </a>
                  {canEdit ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={savingId === display.id}
                      onClick={() => void removeDisplay(display)}
                    >
                      Entfernen
                    </Button>
                  ) : null}

                  {(display.devices?.length ?? 0) > 0 ? (
                    <div className="mt-2 w-full border-t border-separator pt-3">
                      <p className="mb-2 text-xs font-medium text-label-secondary">
                        Registrierte Geräte
                      </p>
                      <ul className="space-y-2">
                        {(display.devices ?? []).map((device) => (
                          <li
                            key={device.id}
                            className="rounded-lg border border-separator bg-background p-3 text-sm"
                          >
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div>
                                <span className="font-medium">{device.device_token_short}</span>
                                <span className="ml-2 rounded-full bg-fill px-2 py-0.5 text-xs">
                                  {deviceStatusLabel(device.status)}
                                </span>
                              </div>
                              {canEdit ? (
                                <div className="flex flex-wrap gap-1">
                                  {device.status === "pending" ? (
                                    <>
                                      <Button
                                        type="button"
                                        size="sm"
                                        disabled={savingId === device.id}
                                        onClick={() =>
                                          void handleDeviceAction(
                                            () => approveInfoScreenDevice(device.id),
                                            device.id,
                                          )
                                        }
                                      >
                                        Freischalten
                                      </Button>
                                      <Button
                                        type="button"
                                        variant="secondary"
                                        size="sm"
                                        disabled={savingId === device.id}
                                        onClick={() => {
                                          if (
                                            !window.confirm(
                                              "Registrierung dieses Geräts verwerfen?",
                                            )
                                          ) {
                                            return;
                                          }
                                          void handleDeviceAction(
                                            () => deleteInfoScreenDevice(device.id),
                                            device.id,
                                          );
                                        }}
                                      >
                                        Verwerfen
                                      </Button>
                                    </>
                                  ) : null}
                                  {device.status === "approved" ? (
                                    <Button
                                      type="button"
                                      variant="secondary"
                                      size="sm"
                                      disabled={savingId === device.id}
                                      onClick={() => {
                                        if (
                                          !window.confirm(
                                            "Gerät sperren? Es zeigt danach keine Inhalte mehr an.",
                                          )
                                        ) {
                                          return;
                                        }
                                        void handleDeviceAction(
                                          () => revokeInfoScreenDevice(device.id),
                                          device.id,
                                        );
                                      }}
                                    >
                                      Sperren
                                    </Button>
                                  ) : null}
                                  {device.status === "revoked" ? (
                                    <Button
                                      type="button"
                                      size="sm"
                                      disabled={savingId === device.id}
                                      onClick={() =>
                                        void handleDeviceAction(
                                          () => approveInfoScreenDevice(device.id),
                                          device.id,
                                        )
                                      }
                                    >
                                      Erneut freischalten
                                    </Button>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                            {canEdit ? (
                              <label className="mt-2 flex flex-col gap-1 text-xs">
                                Bezeichnung (optional)
                                <input
                                  className="eh-input"
                                  placeholder="z. B. Monitor Empfang"
                                  defaultValue={device.label ?? ""}
                                  disabled={savingId === device.id}
                                  onBlur={(e) => void saveDeviceLabel(device, e.target.value)}
                                />
                              </label>
                            ) : device.label ? (
                              <p className="mt-1 text-xs text-label-secondary">{device.label}</p>
                            ) : null}
                            <p className="mt-1 text-xs text-label-tertiary">
                              Zuletzt gesehen: {formatDeviceTime(device.last_seen_at)}
                              {device.approved_at
                                ? ` · Freigeschaltet: ${formatDeviceTime(device.approved_at)}`
                                : ""}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p className="mt-2 w-full border-t border-separator pt-3 text-xs text-label-tertiary italic">
                      Noch keine Geräte registriert. Öffnen Sie die Bildschirm-URL auf dem Gerät.
                    </p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-label-tertiary italic">Noch keine Bildschirme konfiguriert.</p>
          )}

          {canEdit ? (
            <div className="flex flex-col gap-2 border-t border-separator pt-4 sm:flex-row sm:items-end">
              <label className="flex min-w-0 flex-1 flex-col gap-1 text-sm">
                Neuer Bildschirm
                <input
                  className="eh-input"
                  placeholder="z. B. Empfang"
                  value={newLabel}
                  disabled={savingId === "new"}
                  onChange={(e) => setNewLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void addDisplay();
                  }}
                />
              </label>
              <Button type="button" disabled={savingId === "new"} onClick={() => void addDisplay()}>
                Bildschirm hinzufügen
              </Button>
            </div>
          ) : null}
        </div>
      </Card>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}
    </div>
  );
}
